import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { io } from "socket.io-client";
import axiosInstance from "apiConfig";
import { getAuthToken, clearAuthStorage, storeAuthToken } from "./tokenStorage";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [rmId, setRmId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const socketRef = useRef(null);
  const [permissions, setPermissions] = useState({});

  const navigate = useNavigate();

const fetchPermissions = async () => {
  try {
    const token = getAuthToken();
    const response = await axiosInstance.get("/uac/permissions", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.data.success && token === getAuthToken()) {
      setPermissions(response.data.permissions);
      // console.log("Permissions fetched successfully:", response.data.permissions);
    }
  } catch (err) {
    console.error("Failed to fetch permissions:", err);
  }
};

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp * 1000 > Date.now()) {
          // Upgrade existing tab-only sessions without requiring another login.
          storeAuthToken(token);
          setIsAuthenticated(true);
          setUserRole(decodedToken.role);
          setRmId(decodedToken.rm_id || null);
          setUserId(decodedToken.user_id || null);
          setTenantId(decodedToken.tenant_id || null);
          setUserName(decodedToken.name || "User");
          setTenantName(decodedToken.tenant || null);
          setUserEmail(decodedToken.email || null);

          fetchPermissions();
          // Initialize WebSocket connection
          const socketInstance = io(process.env.REACT_APP_WEBSOCKET_URL, {
            transports: ["websocket"],
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
          });

          socketInstance.on("connect", () => {
            // console.log("WebSocket connected:", socketInstance.id);
            socketInstance.emit("registerUser", decodedToken.user_id);
          });

          socketInstance.on("disconnect", () => {
            // console.log("WebSocket disconnected. Attempting to reconnect...");
          });

          socketInstance.on("connect_error", (err) => {
            // console.error("WebSocket connection error:", err);
          });

          socketInstance.on("forceLogout", () => {
            // console.log("User disabled. Logging out...");
            logout();
          });

          socketRef.current = socketInstance;

          return () => {
            socketRef.current?.disconnect();
          };
        } else {
          console.warn("Token expired:", decodedToken.exp * 1000);
          clearAuth();
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        clearAuth();
      }
    }
  }, []);

  useEffect(() => {
    const syncSession = (event) => {
      if (event.storageArea !== localStorage || (event.key !== "token" && event.key !== null)) return;
      if (!localStorage.getItem("token")) {
        clearAuth();
        navigate("/signin", { replace: true });
      } else {
        // Reload permissions, tenant data and sockets for the new account.
        sessionStorage.removeItem("token");
        window.location.reload();
      }
    };
    window.addEventListener("storage", syncSession);
    return () => window.removeEventListener("storage", syncSession);
  }, []);

  const clearAuth = () => {
    setIsAuthenticated(false);
    setUserRole(null);
    setRmId(null);
    setUserId(null);
    setTenantId(null);
    setUserName(null);
    setTenantName(null);
    setUserEmail(null);
    setPermissions({});
    clearAuthStorage();
    socketRef.current?.disconnect();
    socketRef.current = null;
  };

  const login = (token, rememberMe) => {
    try {
      const decodedToken = jwtDecode(token);
      if (!(decodedToken.exp * 1000 > Date.now())) throw new Error("Token expired");
      storeAuthToken(token, rememberMe);
      setPermissions({});
      socketRef.current?.disconnect();
      setIsAuthenticated(true);
      setUserRole(decodedToken.role);
      setRmId(decodedToken.rm_id || null);
      setUserId(decodedToken.user_id || null);
      setTenantId(decodedToken.tenant_id || null);
      setUserName(decodedToken.name || "User");
      setTenantName(decodedToken.tenant || null);
      setUserEmail(decodedToken.email || null);

      fetchPermissions(); 

      // Reconnect WebSocket after login
      const socketInstance = io(process.env.REACT_APP_WEBSOCKET_URL, {
        transports: ["websocket"],
      });

      socketInstance.emit("registerUser", decodedToken.user_id);
      socketRef.current = socketInstance;

      socketInstance.on("forceLogout", () => {
        console.log("User disabled. Logging out...");
        logout();
      });

      navigate("/dashboard");
    } catch (error) {
      console.error("Error during login:", error);
      clearAuth();
    }
  };

  const logout = () => {
    clearAuth();
    navigate("/signin");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        rmId,
        userId,
        tenantId,
        userName,
        tenantName,
        userEmail,
        permissions,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
